<template>
    <div
        :id="id"
        ref="paneEl"
        class="tab-pane"
        :class="{ active: isActive }"
        role="tabpanel"
        :aria-labelledby="`${id}-tab`"
    >
        <slot v-if="hasRendered" />
    </div>
</template>

<script setup>
import {computed, inject, nextTick, onUnmounted, ref, useId, useTemplateRef, watch} from 'vue';
import {baseTabsKey} from './baseTabsKey';
import {initTooltips} from '../../../Services/Tooltip';

const {title, lazy} = defineProps({
    title: {
        type: String,
        required: true,
    },
    lazy: {
        type: Boolean,
        default: false,
    },
});

const tabsApi = inject(baseTabsKey, null);

if (tabsApi === null) {
    throw new Error('<base-tab> must be a descendant of <base-tabs>.');
}

const id = `base-tab-pane-${useId()}`;

const tab = {
    id,
    get title() {
        return title;
    },
};
tabsApi.register(tab);
onUnmounted(() => tabsApi.unregister(tab));

const isActive = computed(() => tabsApi.activeTabId.value === id);

// Lazy-once: a lazy pane mounts its content on first activation and keeps it
// mounted afterwards, so returning to the tab does not re-fetch its data.
const hasRendered = ref(!lazy);
watch(isActive, (active) => {
    if (active) {
        hasRendered.value = true;
    }
}, {immediate: true});

// Server-rendered slot content can carry Bootstrap tooltip triggers; those
// only exist in the DOM once this pane renders, so initialise them here.
const paneEl = useTemplateRef('paneEl');
watch(hasRendered, async (rendered) => {
    if (!rendered) {
        return;
    }
    await nextTick();
    if (paneEl.value) {
        initTooltips(paneEl.value.querySelectorAll('[data-bs-toggle="tooltip"]'));
    }
}, {immediate: true});
</script>
